/**
 * @module shared/common-api
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
// tslint:disable:max-file-line-count
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
import { Injectable } from '@angular/core';
import {
  throwError as observableThrowError,
  Observable,
  of as observableOf,
  ReplaySubject,
} from 'rxjs';
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ContentProviderService } from '@core/aem/services/content-provider.service'; // @TODO remove
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { StatesInterface } from '@support/api/common/states.interface';
import { FieldValidationItem } from '@support/shared/models/field-validation-item.model';
import { SelectItem } from '@support/shared/interfaces/select-item.interface';
import { SubscriberSegmentation } from '@experience/directto-account/interfaces/directto-account.interface';
import { ApiCache } from '@support/shared/interfaces/api-cache-object.interface';
// Constants
import {
  REFERENCE_LIST_TYPES,
  FIELD_VALIDATIONS_CONSTANTS,
  COMMON_API_SERVICE_MODULE_COMPONENTS,
  CONSTANT_COUNTRY_CODES,
} from '@support/shared/services/common-api/common-api.constants';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
// Service
import { LoggerService } from '@core/logger/services/logger.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { APIError } from '@support/api/common/error.interface';
import { PaymentEligibleCountry } from '@support/api/proxies/responses/api-2630.interface';
import { PauseAndResumeResponse } from '@support/api/proxies/responses/api-2831.interface';
import { PauseAndResumeRequest } from '@support/api/proxies/requests/api-2831.interface';
import { map, catchError } from 'rxjs/operators';
import { HttpResponse, HttpHeaders } from '@angular/common/http';
import { OverrideOTPActivityLogRequest } from '@support/api/proxies/requests/api_2832.interface';
import { OverrideOTPActivityLogResponse } from '@support/api/proxies/responses/api_2832.interface';

@Injectable(
  { providedIn: 'root' }
)
export class CommonApiService {
  /**
   * Field validations object stores common field validations for multiple components
   * Mapping of key -> FieldValidationItem
   */
  private fieldValidationItems: Object;
  private referenceListData: Object;
  private states: StatesInterface;
  private uiFieldValidationsLoaded: ReplaySubject<boolean>;
  public uiFieldValidationsLoaded$: Observable<boolean>;
  constructor(
    private apiGateway: ApiGatewayService,
    private contentProviderService: ContentProviderService,
    private logger: LoggerService,
    private stateStoreUtilsService: StateStoreUtilsService
  ) {
    // Initialize field validations to an empty object
    this.fieldValidationItems = {};
    this.referenceListData = {};
    this.uiFieldValidationsLoaded = new ReplaySubject();
    this.uiFieldValidationsLoaded$ = this.uiFieldValidationsLoaded.asObservable();
    this.requestAEMFieldValidation();
  }

  /**
   * Function returns the field validation object for the specified key
   * @param key String identifier to retireve fieldValidation object
   */
  public getFieldValidation(key: string): FieldValidationItem {
    return this.fieldValidationItems[key]
      ? new FieldValidationItem(this.fieldValidationItems[key])
      : null;
  }

  public getReferenceList(type: string[]): void {
    this.logger.debug(
      COMMON_API_SERVICE_MODULE_COMPONENTS.COMMON_API_SERVICE_MODULE_NAME,
      COMMON_API_SERVICE_MODULE_COMPONENTS.COMMON_API_SERVICE,
      'entering getReferenceList() in ' + COMMON_API_SERVICE_MODULE_COMPONENTS.COMMON_API_SERVICE,
      'input param type' + JSON.stringify(type)
    );
    let headers = new HttpHeaders();
    const cacheObj: ApiCache = { preferCache: true, cacheResponse: true };
    const query = type.join(',');
    const payload = {
      type: query,
      customerRole: this.stateStoreUtilsService.getRole(),
    };
    // @TODO localStorage to be replaced by stateStore
    headers = headers.append('roles', this.stateStoreUtilsService.getRole());
    this.apiGateway
      .postRequest(
        ASSISTED_API_MAP.API_2021_getRepDashLookupCriteria,
        payload,
        headers,
        null,
        null,
        cacheObj
      )
      .pipe(map((response: HttpResponse<Object>) => response.body))
      .subscribe((data: Object) => {
        this.logger.debug(
          COMMON_API_SERVICE_MODULE_COMPONENTS.COMMON_API_SERVICE_MODULE_NAME,
          COMMON_API_SERVICE_MODULE_COMPONENTS.COMMON_API_SERVICE,
          'received response for API 2021 for  getReferenceList() in ' +
            COMMON_API_SERVICE_MODULE_COMPONENTS.COMMON_API_SERVICE,
          'data' + JSON.stringify(data)
        );
        this.referenceListData = data ? data : {};
        this.logger.debug(
          COMMON_API_SERVICE_MODULE_COMPONENTS.COMMON_API_SERVICE_MODULE_NAME,
          COMMON_API_SERVICE_MODULE_COMPONENTS.COMMON_API_SERVICE,
          'leaving getReferenceList() in ' + COMMON_API_SERVICE_MODULE_COMPONENTS.COMMON_API_SERVICE
        );
      });
  }

  /**
   * Function fetch response of API 493 for in migration check
   * @param {object} param containing MSISDN number of customer
   */
  public getSubscriberSegmentation(queryParams: SubscriberSegmentation): Observable<Object> {
    const apiConfig = API_MAP.API_493_getSubscriberSegmentation;
    return this.apiGateway.postRequest(apiConfig, queryParams, null).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      })
    );
  }

  /**
   * Method to check list of restrictions
   * @param {BodyParams} reqObj
   * @returns {Observable<PostCustomerVerificationResponse>}
   * @memberof VerificationService
   */
  public getListOfRestrictions(requestPayLoad: Object): Observable<Object> {
    return this.apiGateway
      .postRequest(API_MAP.API_532_getListOfRestrictions, requestPayLoad, null, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        })
      );
  }
  /**
   * Method to get the billing Address by calling API 2062
   * @param reqObj
   * @returns Observable
   */
  public getBillingAddressDetail(reqObj: Object): Observable<Object> {
    return this.apiGateway.postRequest(API_MAP.API_2062_billingAddress, reqObj, null, null).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      })
    );
  }
  /**
   * Method to update  the billing Address by calling API 2063
   * @param reqObj
   * @returns Observable
   */
  public postAddresses(reqObj: Object): Observable<Object> {
    return this.apiGateway
      .postRequest(API_MAP.API_2063_postUpdateAddresses, reqObj, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        })
      );
  }
  /**
   * Method to get the billing address to validate the address
   * @param reqObj
   */
  public postInputValidator(reqObj: Object): Observable<Object> {
    const errorCode = 400;
    return this.apiGateway.postRequest(API_MAP.API_266_postInputValidator, reqObj, null, null).pipe(
      map((response: HttpResponse<Object>) => {
        if (response.status === errorCode) {
          throw new Error('NoMatchFound');
        } else {
          return response.body;
        }
      }),
      catchError(this.handleServerError)
    );
  }

  public postCustomerRequests(
    reqObj: Object,
    loginData: { [name: string]: string }
  ): Observable<Object> {
    return this.apiGateway
      .postRequest(ASSISTED_API_MAP.API_329_postCustomerRequests, reqObj, null, null, loginData)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        })
      );
  }
  /**
   * Function returns the referenceListData object for the spefified key
   * @param key String identifier to retireve referenceListData object
   */
  // TODO: Depricate use of this function and use observable function instead
  public getReferenceListByType(key: string): ObjectInterface[] {
    const type = 'type';
    if (
      this.referenceListData &&
      this.referenceListData[type] &&
      this.referenceListData[type][key]
    ) {
      return this.referenceListData[type][key];
    }
    return null;
  }

  public getFieldValidationConstants(): ObjectInterface {
    return FIELD_VALIDATIONS_CONSTANTS;
  }
  /**
   * Requesting AEM content for RAP redirection
   */
  public requestFieldValidations(): void {
    this.requestAEMFieldValidation();
  }

  /**
   * Requests referenceList through combined API for all required references
   */
  public getReferenceListByTypeObservable(): Observable<Object> {
    const query = REFERENCE_LIST_TYPES.join(',');
    const payload = {
      type: query,
      customerRole: this.stateStoreUtilsService.getRole(),
    };
    let headers = new HttpHeaders();
    // @TODO localStorage to be replaced by stateStore
    headers = headers.append('roles', this.stateStoreUtilsService.getRole());
    const cacheObj: ApiCache = { preferCache: true, cacheResponse: true };
    return this.apiGateway
      .postRequest(
        ASSISTED_API_MAP.API_2021_getRepDashLookupCriteria,
        payload,
        headers,
        null,
        null,
        cacheObj
      )
      .pipe(map((response: HttpResponse<Object>) => response.body));
  }

  /**
   * Requests referenceList through combined API for all required references parameter
   */
  public getReferenceListByParameterTypeObservable(param: string): Observable<Object> {
    const query = param ? param : REFERENCE_LIST_TYPES.join(',');
    let headers = new HttpHeaders();
    headers = headers.append('roles', this.stateStoreUtilsService.getRole());
    const cacheObj: ApiCache = { preferCache: true, cacheResponse: true };
    const payload = {
      type: query,
      customerRole: this.stateStoreUtilsService.getRole(),
    };
    return this.apiGateway
      .postRequest(
        ASSISTED_API_MAP.API_2021_getRepDashLookupCriteria,
        payload,
        headers,
        null,
        null,
        cacheObj
      )
      .pipe(map((response: HttpResponse<Object>) => response.body));
  }
  /**
   * Function returns an observable of referenceList types constructed for dropdowns
   * @param referenceListType : Key identified for referernceList
   * @param label : label for each selectItem
   * @param value : value of each selectItem
   */
  public getSelectItemArrayObservable(
    referenceListType: string,
    label: string,
    value: string
  ): Observable<SelectItem[]> {
    // TODO: Add Observable return value for states
    // if (referenceListType !== 'states') {
    return this.getReferenceListByTypeObservable().pipe(
      map((data: Object) => {
        const referenceListArray: SelectItem[] = [];
        const type = 'type';
        if (data && data[type] && data[type][referenceListType]) {
          for (const item of data[type][referenceListType]) {
            referenceListArray.push({ label: item[label], value: item[value] });
          }
        }
        return referenceListArray;
      })
    );
    // TODO: Need to refactor for getting states as an observable
  }

  // TODO: Depricate use of this function and use observable instead
  public getSelectItemArray(referenceListType: string, label: string, value: string): SelectItem[] {
    const tempArray: SelectItem[] = [];
    let responseArray: ObjectInterface[];
    responseArray = this.getReferenceListByType(referenceListType);
    if (responseArray) {
      for (const item of responseArray) {
        tempArray.push({ label: item[label], value: item[value] });
      }
    }
    return tempArray;
  }

  /**
   * Requests AEM for field validations json and stores data in the private service object
   */
  private requestAEMFieldValidation(): void {
    this.contentProviderService
      .getRequest('/content/global/fieldValidations/en/uiFieldValidations.content.json')
      .pipe(map((response: HttpResponse<Object>) => response.body))
      .subscribe((data: Object) => {
        this.fieldValidationItems = data ? data : {};
        this.uiFieldValidationsLoaded.next(true);
      });
  }

  /**
   * Make 348 API call to get list of all the states if not available
   * @return Observable<SelectItem[]>
   */
  public getStates(
    label: string,
    value: string,
    selectedCountry?: string
  ): Observable<SelectItem[]> {
    if (!this.states) {
      const headers = new HttpHeaders();
      const cacheObj: ApiCache = { preferCache: true, cacheResponse: true };
      const payload = {
        countryCode: selectedCountry,
        stateType: 'all',
      };
      return this.apiGateway
        .postRequest(API_MAP.API_348_getStatesFromType, payload, headers, null, null, cacheObj)
        .pipe(
          map((response: HttpResponse<Object>) => {
            this.states = <StatesInterface>(response.body ? response.body : {});
            return this.getStatesAsList(this.states, label, value);
          })
        );
    } else {
      return observableOf(this.getStatesAsList(this.states, label, value));
    }
  }

  /**
   * Make 2061 API call to get list of all the states w.r.t. country code if not available
   * @return Observable<SelectItem[]>
   */
  public getCountryStates(
    label: string,
    value: string,
    selectedCountry: string = CONSTANT_COUNTRY_CODES.USA
  ): Observable<SelectItem[]> {
    const headers = new HttpHeaders();
    const payload = {
      countryCode: selectedCountry,
      stateType: 'all',
    };
    const cacheObj: ApiCache = { preferCache: true, cacheResponse: true };
    return this.apiGateway
      .postRequest(API_MAP.API_2061_getStates, payload, headers, null, null, cacheObj)
      .pipe(
        map((response: HttpResponse<Object>) => {
          this.states = <StatesInterface>response.body;
          return this.getStatesAsList(this.states, label, value);
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * method to make a call of API2630
   * @returns {Observable<PaymentEligibleCountry>}
   */
  public paymentEligibleCountires(): Observable<PaymentEligibleCountry> {
    const cacheObj: ApiCache = { preferCache: true, cacheResponse: true };
    // Added request changes for get to post api conversion
    return this.apiGateway
      .postRequest(API_MAP.API_2630_getPaymentEligibleCountries, {}, null, null, null, cacheObj)
      .pipe(
        map((response: HttpResponse<PaymentEligibleCountry>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Get list of states as SelectItem array
   * @param data: List of states
   * @param label: Value to be used as Label for Select List
   * @param value: Value to be used as Value for Select List
   * @returns SelectItem[]
   */
  private getStatesAsList(data: StatesInterface, label: string, value: string): SelectItem[] {
    const tempArray: SelectItem[] = [];
    if (data && data.states && data.states.length) {
      for (const item of data.states) {
        tempArray.push({ label: item[label], value: item[value] });
      }
    }
    return tempArray;
  }

  private handleServerError(error: Response): Observable<string> {
    return observableThrowError(error);
  }

  /**
   * Method to send the pause/resume request for any associated recordings
   * @param pause boolean that determines what key to send (Pause/Resume)
   * @returns {Observable<PauseAndResumeResponse>} observable pause and resume response
   */
  public postPauseAndResume(pause: boolean): Observable<PauseAndResumeResponse> {
    const requestPayload: PauseAndResumeRequest = {
      key: pause ? 'Pause' : 'Resume',
    };

    return this.apiGateway
      .postRequest(
        API_MAP.API_2831_postPauseAndResume,
        requestPayload,
        null,
        null,
        null,
        null,
        false
      )
      .pipe(
        map((resp: HttpResponse<PauseAndResumeResponse>) => {
          return resp.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Used to Post Activity logs
   * @returns
   */
  public postActivityLog(): Observable<OverrideOTPActivityLogResponse> {
    const requestPayload: OverrideOTPActivityLogRequest = {
      customerId: this.stateStoreUtilsService.getBscsCustomerID(),
      accountId: this.stateStoreUtilsService.getAccountId(),
      losId: this.stateStoreUtilsService.getLineId(),
      msisdn: this.stateStoreUtilsService.getMsisdn(),
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2832_overrideOTP, requestPayload, null, null, null, null, false)
      .pipe(
        map((resp: HttpResponse<OverrideOTPActivityLogResponse>) => {
          return resp.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }
}
